import { FastForward } from "lucide-react";
import { useEffect, useRef } from "react";
import { type CanaryRegistry, describeHits } from "../core/canary";
import type { AuditEvent, Category, Severity, SourceSite } from "../core/events";
import {
  CATEGORY_LABELS,
  describeEvent,
  formatSite,
  formatVirtualTime,
  layerDepths,
  rowIndent,
  type TimelineState,
  timelineRows,
} from "../core/timeline";

/** Literal class names, so Tailwind can see them. */
const CATEGORY_STYLE: Record<Category, string> = {
  network: "text-cat-network border-cat-network",
  dom: "text-cat-dom border-cat-dom",
  storage: "text-cat-storage border-cat-storage",
  globals: "text-cat-globals border-cat-globals",
  probe: "text-cat-probe border-cat-probe",
  codegen: "text-cat-codegen border-cat-codegen",
  timers: "text-cat-timers border-cat-timers",
  fingerprint: "text-cat-fingerprint border-cat-fingerprint",
  evasion: "text-cat-evasion border-cat-evasion",
  messaging: "text-cat-messaging border-cat-messaging",
  lifecycle: "text-fg-dark border-fg-dark",
};

const SEVERITY_STYLE: Record<Severity, string> = {
  info: "text-comment",
  notable: "text-blue",
  suspicious: "text-orange",
  critical: "text-red",
};

/** Rendering every row of a 50k-event run would stall the page; virtualize later. */
const MAX_ROWS = 2000;

interface TimelinePaneProps {
  timeline: TimelineState;
  /** Click-to-source: jump the editor to a row's site (switching layer tabs). */
  onReveal?: (site: SourceSite) => void;
  /** Highlight a hovered row's line, or clear it with null. */
  onHover?: (site: SourceSite | null) => void;
  /** A row to scroll to and mark (e.g. a Report evidence link); a new object re-scrolls. */
  highlight?: { seq: number } | null;
}

/** Pixels of indent per code-layer level. */
const INDENT_PX = 16;

/**
 * The chronological event stream (docs/ui-design.md §3); filters arrive
 * later. Events from eval'd code are indented by their layer's depth, so they
 * sit under the codegen row that revealed the layer.
 */
export function TimelinePane({ timeline, onReveal, onHover, highlight }: TimelinePaneProps) {
  const { events } = timeline;
  const depths = layerDepths(timeline.layers);
  if (events.length === 0) {
    return (
      <p data-testid="timeline" className="px-3 py-2 text-comment">
        {timeline.status === "running" ? "Waiting for events…" : "No events recorded."}
      </p>
    );
  }
  const shown = events.length > MAX_ROWS ? events.slice(0, MAX_ROWS) : events;
  return (
    <div data-testid="timeline" className="font-mono text-[13px]">
      <ol aria-label="Timeline events">
        {timelineRows(shown).map((row) =>
          row.kind === "event" ? (
            <EventRow
              key={row.event.seq}
              event={row.event}
              indent={rowIndent(row.event, depths)}
              highlight={highlight?.seq === row.event.seq ? highlight : null}
              registry={timeline.canaries}
              onReveal={onReveal}
              onHover={onHover}
            />
          ) : (
            <li
              key={`skip-${row.to}`}
              data-testid="time-skip"
              className="flex items-center gap-2 border-b border-border/40 bg-bg-dark px-3 py-0.5 text-cat-timers"
            >
              <FastForward aria-hidden="true" className="size-3.5 shrink-0" />
              <span>skipped {formatVirtualTime(row.to - row.from)} of virtual time</span>
            </li>
          ),
        )}
      </ol>
      {events.length > MAX_ROWS && (
        <p className="px-3 py-1 text-comment">
          Showing the first {MAX_ROWS} of {events.length} events.
        </p>
      )}
    </div>
  );
}

function EventRow({
  event,
  indent,
  highlight,
  registry,
  onReveal,
  onHover,
}: {
  event: AuditEvent;
  indent: number;
  highlight: { seq: number } | null;
  /** The run's canaries, once known, to name canary hits by source. */
  registry: CanaryRegistry;
  onReveal: ((site: SourceSite) => void) | undefined;
  onHover: ((site: SourceSite | null) => void) | undefined;
}) {
  const { site } = event;
  const row = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (highlight) row.current?.scrollIntoView({ block: "nearest" });
  }, [highlight]);
  return (
    <li
      ref={row}
      data-category={event.category}
      data-layer={event.layer}
      data-seq={event.seq}
      data-severity={event.severity}
      aria-current={highlight ? "true" : undefined}
      onMouseEnter={site && onHover ? () => onHover(site) : undefined}
      onMouseLeave={site && onHover ? () => onHover(null) : undefined}
      className={`flex items-baseline gap-2 border-b border-border/40 px-3 py-0.5 hover:bg-bg-highlight ${highlight ? "row-flash bg-bg-highlight outline outline-1 outline-blue" : ""}`}
    >
      <span className="w-14 shrink-0 text-right tabular-nums text-comment">
        {formatVirtualTime(event.t)}
      </span>
      {indent > 0 && (
        <span
          aria-hidden="true"
          data-testid="layer-indent"
          className="shrink-0 self-stretch border-l border-cat-codegen/50"
          style={{ width: indent * INDENT_PX }}
        />
      )}
      <span
        className={`w-18 shrink-0 rounded border px-1 text-center text-[11px] font-semibold ${CATEGORY_STYLE[event.category]}`}
      >
        {CATEGORY_LABELS[event.category]}
      </span>
      <span className="min-w-0 flex-1 truncate" title={describeEvent(event)}>
        {describeEvent(event)}
      </span>
      {event.canaries && event.canaries.length > 0 && (
        <span
          data-testid="canary-badge"
          title={`Canary: ${describeHits(event.canaries, registry)}`}
          className="shrink-0 rounded border border-red px-1 text-[11px] font-semibold text-red"
        >
          canary
        </span>
      )}
      {event.severity !== "info" && (
        <span className={`shrink-0 text-[11px] ${SEVERITY_STYLE[event.severity]}`}>
          {event.severity}
        </span>
      )}
      {site &&
        (onReveal ? (
          <button
            type="button"
            data-testid="site-link"
            title="Show in the editor"
            onClick={() => onReveal(site)}
            onFocus={() => onHover?.(site)}
            onBlur={() => onHover?.(null)}
            className="shrink-0 rounded text-comment underline-offset-2 hover:text-fg hover:underline focus-visible:outline-2 focus-visible:outline-blue"
          >
            {formatSite(site)}
          </button>
        ) : (
          <span className="shrink-0 text-comment">{formatSite(site)}</span>
        ))}
    </li>
  );
}
